import { ErrorHandler, Injectable, Injector } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { ErrorService } from "./services/global-error/error.service";
import { LoggingService } from "./services/global-error/logging.service";
import { NotificationService } from "./services/global-error/notification.service";

/**
 * Global error handler. Does NOT navigate to the error page.
 * Error page is only shown when explicitly triggered by:
 * - DashboardService (validateUser failure)
 * - getLoginUserInfo failure (via ServerErrorInterceptor)
 * - Any other place that explicitly sets messages and navigates to /error.
 */
@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

    constructor(private injector: Injector) { }

    handleError(error: any | HttpErrorResponse): void {
        /*
        * AG Grid Bug Workaround: Suppress a recurring TypeError from the internal TouchListener.
        * The root cause is a project dependency conflict, where multiple, incompatible versions
        * of AG Grid (v32 and v33) are loaded simultaneously. This version mismatch causes the
        * grid's Header Component logic to fail its internal DOM query for an element to attach
        * an event listener to, resulting in a null reference. This workaround is required as
        * the project's dependencies cannot be modified to resolve the conflict.
        */
        const isAgGridTouchError = error.message?.includes("Cannot read properties of null (reading 'addEventListener')")
            && error.stack?.includes('TouchListener');

        // If it's the known AG Grid bug, do nothing and exit the function immediately.
        if (isAgGridTouchError) {
            return;
        }
        const errorService = this.injector.get(ErrorService);
        const logger = this.injector.get(LoggingService);
        const notifier = this.injector.get(NotificationService);

        if (error instanceof HttpErrorResponse) {
            // Server errors: log only; do not navigate. Callers (e.g. dashboard, getLoginUserInfo) handle /error.
            if (error.status === 500) {
                const message = errorService.getServerErrorMessage(error);
                console.error('[GlobalErrorHandler] Server error (500):', message);
            }
            return;
        }
        {
            // Client errors: skip logging when no stack or when stack is a known noisy one (NgScrollbar / getElementRef).
            const stack = error?.stack;
            if (!stack || stack.includes('at NgScrollbar.get') || stack.includes("reading 'getElementRef'")) {
                return;
            }
            const message = errorService.getClientErrorMessage(error);
            console.error('[GlobalErrorHandler] Client error:', message);
            console.error(error);
        }
    }
}